/// <amd-module name="Wit/QueriesHub/Common/Utils/QueryUtils" />
import { IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ago, localeDateFormat } from "VSS/Platform/Date";
import { IListSelection } from "VSSUI/List";
import { Tree } from "VSSUI/TreeEx";
import { TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IQueryService, QueryHubView } from "Wit/Common/QueryService/IQueryService";
import { IQueryContext, IQueryResultItem } from "Wit/QueriesHub/Common/Interfaces";
import * as Resources from "Wit/QueriesHub/Resources";

export class QueryUtils {
    public static getQueryTypeIcon(queryType: QueryType): string {
        switch (queryType) {
            case QueryType.Tree:
                return "ViewListTree";
            case QueryType.OneHop:
                return "ViewListGroup";
            case QueryType.Flat:
                return "ViewList";
            default:
                // This can happen if the wiql is invalid, rest api doesnt populate queryType
                return "Error";
        }
    }

    public static getQueryTypeShortText(queryType: QueryType, hasError?: boolean): string {
        if (hasError) {
            return Resources.InvalidQuery;
        }

        switch (queryType) {
            case QueryType.Tree:
                return Resources.TreeLinksQuery;
            case QueryType.OneHop:
                return Resources.DirectLinksQuery;
            default:
                return Resources.FlatQuery;
        }
    }

    public static getFriendlyDate(date: Date, limit: number) {
        const diff = (new Date().getTime() - date.getTime()) / 1000;

        //change date and time to user time zone setting
        const dateInUserTime = getDateInUserTimeZone(date);

        if (diff < limit) {
            return ago(dateInUserTime, undefined, getDateInUserTimeZone());
        } else {
            return localeDateFormat(dateInUserTime, "d");
        }
    }

    public static getColumnHeaderPadding = () => {
        // Table headers have 0.6875 rem of padding on either side
        return 0.6875 * parseFloat(getComputedStyle(document.documentElement).fontSize!) * 2;
    };

    public static getSelectedItems = (selection: IListSelection, queryResultItems: IQueryResultItem[]): IQueryResultItem[] => {
        const selectedItems: IQueryResultItem[] = [];
        if (queryResultItems.length) {
            for (let range of selection.value) {
                for (let i = range.beginIndex; i <= range.endIndex; i++) {
                    const item = queryResultItems[i];
                    if (item) {
                        selectedItems.push(item);
                    }
                }
            }
        }

        return selectedItems;
    };

    public static getSelectedItemsFromTreeItemProvider = (
        selection: IListSelection,
        items: TreeItemProvider<IQueryResultItem | undefined>
    ): IQueryResultItem[] => {
        const selectedItems: IQueryResultItem[] = [];
        if (items.value) {
            for (let range of selection.value) {
                for (let i = range.beginIndex; i <= range.endIndex; i++) {
                    const item = items.value[i];
                    if (item) {
                        selectedItems.push(item.underlyingItem.data as IQueryResultItem);
                    }
                }
            }
        }

        return selectedItems;
    };

    public static getDefaultQueryContext = (wiql: string, tempQueryId?: string): IQueryContext => {
        const id = tempQueryId || "00000000-0000-0000-0000-000000000000";
        const name = tempQueryId ? Resources.CustomQuery : Resources.UntitledQuery;
        return { id, isPublic: false, name, wiql, path: null };
    };

    public static getQueryContext = async (queryService: IQueryService, projectId: string): Promise<IQueryContext> => {
        const currentWiql = queryService.wiql.value;
        const [query, wiql] = await Promise.all([
            queryService.getQuery(projectId),
            currentWiql ? Promise.resolve(currentWiql) : queryService.generateWiql(projectId)
        ]);
        if (query) {
            return query;
        } else {
            return QueryUtils.getDefaultQueryContext(wiql, queryService.tempQueryId);
        }
    };

    public static onFocusNext = (
        treeRef: React.RefObject<Tree<IQueryResultItem>>,
        lastFocusedRow: ObservableValue<number>,
        itemsLength: number,
        selection: IListSelection
    ) => {
        if (treeRef.current && lastFocusedRow.value >= 0 && lastFocusedRow.value < itemsLength - 1) {
            const index = lastFocusedRow.value + 1;
            treeRef.current.focusRow(index);
            selection.select(index, 1, false);
        }
    };

    public static onFocusPrevious = (
        treeRef: React.RefObject<Tree<IQueryResultItem>>,
        lastFocusedRow: ObservableValue<number>,
        selection: IListSelection
    ) => {
        if (treeRef.current && lastFocusedRow.value > 0) {
            const index = lastFocusedRow.value - 1;
            treeRef.current.focusRow(index);
            selection.select(index, 1, false);
        }
    };

    public static insertToFrontIfNotFound(arr: string[], targets: string[]): string[] {
        return targets.filter(x => !arr.includes(x)).concat(arr);
    }

    public static focusInitialRow(
        queryService: IQueryService,
        treeRef: React.RefObject<Tree<IQueryResultItem> | null>,
        lastFocusedRow: ObservableValue<number>,
        items: IReadonlyObservableArray<IQueryResultItem>,
        selection: IListSelection
    ) {
        if (items.value.length && (lastFocusedRow.value < 0 || lastFocusedRow.value > items.value.length - 1) && treeRef.current) {
            // best effort to set the focused row to the last work item that was being edited
            let indexToFocus = 0;
            const idToFocus = queryService.lastEditedWorkItem ?? 0;
            if (queryService.previousView === QueryHubView.WorkItemEdit && idToFocus > 0) {
                indexToFocus = Math.max(
                    0,
                    items.value.findIndex(item => item.value[CoreFieldRefNames.Id] === idToFocus)
                );
            }

            treeRef.current.focusRow(indexToFocus);
        }
    }
}
